/**
 *
 * QTag
 *
 */

import classNames from 'classnames';
import { Record } from 'immutable';
import { omit } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { withStyles } from 'tss-react/mui';

import { tagsTypes } from 'app-components/flux/tags';

import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import DoneIcon from '@mui/icons-material/Done';
import Chip from '@mui/material/Chip';
import Input from 'components/QTag/Input';

const styles = ({ spacing, palette }) => ({
  QTag: {
    overflow: 'hidden',
  },
  chipRoot: {
    position: 'relative',
    backgroundColor: palette.background.layer1,
    '&:hover': {
      backgroundColor: palette.action.hover,
    },
    '&:hover input': {
      maxWidth: '90%',
      marginRight: '20%',
      cursor: 'pointer',
    },
    '& > svg': {
      position: 'absolute',
      display: 'none',
      width: spacing(3),
      height: spacing(3),
      right: 0,
    },
    '&:hover > svg': {
      display: 'block',
    },
  },
  disabledRoot: {
    position: 'relative',
    backgroundColor: palette.background.layer1,
    '&:hover input': {
      maxWidth: '90%',
      marginRight: '20%',
      cursor: 'default',
    },
    '& > svg': {
      position: 'absolute',
      display: 'none',
      width: spacing(3),
      height: spacing(3),
      right: 0,
    },
    '&:hover > svg': {
      display: 'block',
    },
  },
  chipOutlined: {
    borderColor: palette.common.black,
    '&:focus': {
      backgroundColor: 'inherit !important',
    },
  },
  outlinedIcon: {
    borderRadius: '50%',
    backgroundColor: palette.secondary.main,
    color: `${palette.common.white} !important`,
    width: spacing(2.5),
    height: spacing(2.5),
    padding: 1,
    '&:hover': {
      backgroundColor: palette.secondary.dark,
    },
  },
  disabledAddBtn: {
    backgroundColor: palette.action.disabled,
    cursor: 'default',
    '&:hover': {
      backgroundColor: palette.action.disabled,
    },
  },
});

export const variants = Object.freeze({
  NORMAL: 'normal',
  EDIT: 'edit',
  NEW: 'new',
});

class QTag extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      tagVariant: this.props.tagVariant,
      tagValue: this.props.tagValue,
      initialValue: this.props.tagValue,
    };

    this.chipRef = React.createRef();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.tagVariant !== this.props.tagVariant) {
      this.setState({ tagVariant: nextProps.tagVariant });
    }
    if (nextProps.tagValue !== this.props.tagValue) {
      this.setState({ tagValue: nextProps.tagValue, initialValue: nextProps.tagValue, tagVariant: variants.NORMAL });
    }
  }

  changeVariant(tagVariant = variants.NORMAL) {
    if (this.props.changeUiState) {
      this.props.changeUiState(this.props.id, tagVariant);
    }
  }

  handleChange = (e, field) => {
    const { value } = e.target;
    const newState = { ...this.state, [field]: value };
    this.setState(newState);
    this.props.setEditingTag(tagsTypes.mkTag({ id: this.props.id, name: newState.tagValue }));
  };

  onCancel = () => {
    const { tagValue, tagVariant, initialValue } = this.state;

    const cancelCallback = () => {
      if (tagVariant === variants.EDIT) {
        this.setState({ tagValue: this.state.initialValue });
        this.changeVariant();
      }
      if (tagVariant === variants.NEW) {
        this.props.removeNewItem();
      }
    };

    // check if data changed and show popup
    if (tagValue === initialValue) {
      cancelCallback();
    } else if (tagValue.trim() !== '') {
      this.props.showSaveChangesDialog(this.onSave, cancelCallback, tagValue);
    } else {
      cancelCallback();
    }
  };

  onChange = (e) => {
    this.handleChange(e, 'tagValue');
  };

  onEdit = () => {
    this.changeVariant(variants.EDIT);
  };

  onKeyDown = (event) => {
    if (event.key === 'Enter') {
      this.onSave();
    } else if (event.key === 'Escape') {
      this.onCancel();
    }
  };

  onSave = () => {
    const tagValue = this.state.tagValue.trim();
    if (tagValue !== '') {
      this.props.handleSave(this.props.id, tagValue, () => {
        this.changeVariant();
        this.chipRef.current.blur();
        this.setState({ initialValue: tagValue });
      });
    }
  };

  onDelete = () => {
    this.props.handleDelete(this.props.id);
  };

  getProps() {
    const { classes, classname, readOnly } = this.props;
    const { tagValue, tagVariant } = this.state;
    const baseId = `tag-${this.props.index}`;
    let inputId = '';
    let newPlaceholder = '';

    const QTagProps = Record({
      id: baseId,
      key: baseId,
      className: [classname, classes.QTag].join(' '),
      classes: {},
      label: undefined,
      deleteIcon: undefined,
      onClick: undefined,
      onDelete: undefined,
      variant: 'default',
      clickable: false,
    });

    let itemProps = new QTagProps();

    switch (tagVariant) {
      case variants.NORMAL:
        inputId = `${baseId}-normal-field`;
        itemProps = new QTagProps({
          deleteIcon: <CancelIcon id={`${baseId}-delete-button`} />,
          clickable: !readOnly,
          onClick: readOnly ? null : this.onEdit, // edit mode
          onDelete: this.onDelete, // delete
          classes: { root: readOnly ? classes.disabledRoot : classes.chipRoot },
        });
        break;
      case variants.EDIT:
        inputId = `${baseId}-edit-field`;
        newPlaceholder = 'Edit Tag';
        itemProps = new QTagProps({
          deleteIcon: <DoneIcon id={`${baseId}-save-button`} />,
          variant: 'outlined',
          onDelete: this.onSave, // save edit
          classes: { outlined: classes.chipOutlined, deleteIcon: classes.outlinedIcon },
        });
        break;
      case variants.NEW:
        {
          const disableTag = tagValue.trim() === '';
          const addIconClasses = classNames({ [classes.disabledAddBtn]: disableTag });
          inputId = `${baseId}-new-field`;
          newPlaceholder = 'New Tag';
          itemProps = new QTagProps({
            deleteIcon: <AddIcon id={`${baseId}-create-button`} className={addIconClasses} />,
            variant: 'outlined',
            onDelete: this.onSave, // save new
            classes: { outlined: classes.chipOutlined, deleteIcon: classes.outlinedIcon },
          });
        }
        break;
      default:
        break;
    }

    const qTagItemProps = itemProps.toJS();
    return {
      inputId,
      newPlaceholder,
      props: qTagItemProps.onClick === undefined ? omit(qTagItemProps, ['onClick']) : qTagItemProps,
    };
  }

  render() {
    const { tagValue, tagVariant } = this.state;
    const isEditing = tagVariant !== variants.NORMAL;
    const { props, newPlaceholder, inputId } = this.getProps();

    const QTagRender = (
      <div>
        <Chip
          ref={this.chipRef}
          onBlur={this.onCancel}
          {...props}
          label={
            <Input
              id={inputId}
              key={`${inputId}-key`}
              isEditing={isEditing}
              onChange={this.onChange}
              onKeyDown={this.onKeyDown}
              tagValue={tagValue}
              placeholder={newPlaceholder}
              hideHiddenSpan
            />
          }
        />
      </div>
    );

    return QTagRender;
  }
}

QTag.defaultProps = {
  readOnly: false,
  classname: '',
  tagVariant: variants.NORMAL,
};

QTag.propTypes = {
  readOnly: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  classname: PropTypes.string,
  changeUiState: PropTypes.func,
  id: PropTypes.string,
  index: PropTypes.number,
  handleSave: PropTypes.func,
  handleDelete: PropTypes.func,
  removeNewItem: PropTypes.func,
  showSaveChangesDialog: PropTypes.func,
  tagValue: PropTypes.string.isRequired,
  tagVariant: PropTypes.oneOf(Object.values(variants)),
  setEditingTag: PropTypes.func,
};

export default withStyles(QTag, styles);
